import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Globe, Instagram } from 'lucide-react';
import { PARTNER_ICONS } from '../lib/partnerIcons';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LogoBadge = ({ name }) => {
  const words = name.split(/\s+/);
  return (
    <div className="flex flex-col items-center gap-2.5">
      <span className="grid place-items-center w-14 h-14 rounded-2xl bg-gradient-to-br from-[hsl(var(--brand))] to-[#1e3a5f] text-white font-serif text-[26px] shadow-lg shadow-[hsl(var(--brand)/0.25)]">{name.charAt(0)}</span>
      <span className="text-center leading-tight">
        <span className="block font-semibold tracking-wide text-[17px] text-[#14304f] dark:text-sky-100">{words[0]}</span>
        {words.length > 1 && <span className="block text-[9.5px] tracking-[0.3em] uppercase text-neutral-500 mt-0.5">{words.slice(1).join(' ')}</span>}
      </span>
    </div>
  );
};

const PartnerCard = ({ p, i }) => (
  <article data-testid={`partner-card-${p.id}`} style={{ animationDelay: `${i * 80}ms` }}
    className="page-fade-in group flex flex-col items-center text-center rounded-2xl bg-white dark:bg-[#141416] border border-neutral-200/70 dark:border-neutral-800 shadow-[0_8px_30px_-12px_rgba(15,30,60,0.18)] hover:shadow-[0_18px_40px_-14px_rgba(15,30,60,0.28)] hover:-translate-y-1 transition-[transform,box-shadow] duration-300 px-6 pt-8 pb-7">
    <div className="h-24 w-full flex items-center justify-center">
      {p.logo
        ? <img src={p.logo} alt={`${p.name} logo`} data-testid={`partner-logo-img-${p.id}`} className="max-h-24 max-w-[170px] object-contain mix-blend-multiply dark:mix-blend-normal dark:[filter:invert(1)_hue-rotate(180deg)_saturate(1.2)] transition-[filter] duration-300" loading="lazy" />
        : <LogoBadge name={p.name} />}
    </div>
    <div className="w-10 h-px bg-neutral-200 dark:bg-neutral-700 my-5" />
    <h3 data-testid={`partner-name-${p.id}`} className="font-semibold text-[16.5px] text-[#14304f] dark:text-neutral-100">{p.name}</h3>
    {p.description && <p className="mt-2.5 text-[13.5px] leading-relaxed text-neutral-600 dark:text-neutral-400 flex-1">{p.description}</p>}
    {(p.website || p.instagram) && (
      <div className="mt-5 flex items-center justify-center gap-2">
        {p.website && <a href={p.website} target="_blank" rel="noopener noreferrer" data-testid={`partner-website-${p.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 dark:border-neutral-700 px-3 py-1.5 text-[12px] text-[#14304f] dark:text-neutral-200 hover:border-brand hover:text-brand transition-colors"><Globe size={13} /> Visit website</a>}
        {p.instagram && <a href={`https://instagram.com/${p.instagram}`} target="_blank" rel="noopener noreferrer" aria-label={`${p.name} on Instagram`} data-testid={`partner-instagram-${p.id}`} className="grid place-items-center w-8 h-8 rounded-full border border-neutral-200 dark:border-neutral-700 text-[#14304f] dark:text-neutral-200 hover:border-pink-500 hover:text-pink-500 transition-colors"><Instagram size={14} /></a>}
      </div>
    )}
    {p.tag && <span className="mt-5 inline-block whitespace-nowrap rounded-full bg-[#eef3f8] dark:bg-neutral-800 px-3.5 py-1.5 text-[10px] font-semibold tracking-[0.08em] uppercase text-[#3b5675] dark:text-neutral-300">{p.tag}</span>}
  </article>
);

const Hero = ({ page }) => (
  <section data-testid="partners-hero" className="relative overflow-hidden bg-[#0b1220] text-white">
    {page.hero_image && (
      <div aria-hidden className="absolute inset-0 bg-cover bg-right" style={{ backgroundImage: `url(${page.hero_image})` }} />
    )}
    <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-[#0b1220] via-[#0b1220]/90 md:via-[#0b1220]/75 to-[#0b1220]/10" />
    <div className="relative max-w-7xl mx-auto px-6 py-16 sm:py-20 lg:py-24 grid lg:grid-cols-12 gap-8 items-center">
      <div className="lg:col-span-7">
        {page.eyebrow && (
          <div className="flex items-center gap-4 text-[11.5px] tracking-[0.4em] uppercase text-sky-100/80 page-fade-in">
            {page.eyebrow}<span className="h-px w-16 bg-sky-100/40" />
          </div>
        )}
        <h1 data-testid="partners-heading" className="mt-5 text-4xl sm:text-5xl lg:text-[3.25rem] font-semibold leading-[1.15] tracking-tight page-fade-in" style={{ animationDelay: '80ms' }}>
          {page.heading}{page.heading_accent && <><br /><span className="text-sky-200">{page.heading_accent}</span></>}
        </h1>
        {page.intro && <p className="mt-6 max-w-xl text-[15.5px] leading-relaxed text-white/85 page-fade-in" style={{ animationDelay: '160ms' }}>{page.intro}</p>}
      </div>
      {page.script_text && (
        <div className="hidden lg:block lg:col-span-3 page-fade-in" style={{ animationDelay: '240ms' }}>
          <p data-testid="partners-script" className="-rotate-12 text-[38px] leading-[1.15] text-white/90" style={{ fontFamily: "'Great Vibes', cursive" }}>{page.script_text}</p>
          <svg viewBox="0 0 200 20" className="w-48 mt-2 -rotate-6 text-white/60" fill="none"><path d="M2 16 C 60 6, 120 4, 198 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
        </div>
      )}
    </div>
  </section>
);

const Highlights = ({ items }) => (
  <section data-testid="partners-highlights" className="mt-12 rounded-2xl bg-[#eef3f8] dark:bg-[#141416] dark:border dark:border-neutral-800 px-6 py-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-y-8">
    {items.map((h, i) => {
      const { Icon } = PARTNER_ICONS[h.icon] || PARTNER_ICONS.shield;
      return (
        <div key={i} data-testid={`partners-highlight-${i}`} className={`flex items-start gap-4 px-4 lg:px-8 ${i % 4 !== 0 ? 'lg:border-l border-[#cfdae6] dark:border-neutral-700' : ''}`}>
          <Icon size={30} strokeWidth={1.4} className="shrink-0 text-[#14304f] dark:text-sky-200" />
          <div>
            <h4 className="font-semibold text-[14.5px] text-[#14304f] dark:text-neutral-100">{h.title}</h4>
            {h.text && <p className="mt-1 text-[12.5px] leading-relaxed text-neutral-600 dark:text-neutral-400">{h.text}</p>}
          </div>
        </div>
      );
    })}
  </section>
);

const PartnersPage = () => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  useEffect(() => {
    axios.get(`${API}/partners`)
      .then(({ data: d }) => { setData(d); setStatus('ok'); document.title = 'Our Partners · Healsglobe Exports'; window.scrollTo(0, 0); })
      .catch(() => setStatus('missing'));
  }, []);

  if (status === 'loading') return <div className="max-w-7xl mx-auto px-6 py-16"><div className="h-72 rounded-2xl bg-neutral-100 dark:bg-neutral-900 animate-pulse" /></div>;
  if (status === 'missing') return (
    <main className="max-w-3xl mx-auto px-6 py-20" data-testid="partners-not-found">
      <h1 className="font-serif text-4xl sm:text-5xl text-neutral-900 dark:text-neutral-100">Page not found</h1>
      <Link to="/" className="mt-6 inline-block text-brand underline">Back to shop</Link>
    </main>
  );

  const { page, partners } = data;
  return (
    <main data-testid="partners-page">
      <Hero page={page} />
      <div className="bg-[#f6f8fb] dark:bg-transparent">
        <div className="max-w-7xl mx-auto px-6 py-14">
          {partners.length > 0 && (
            <>
              <div className="flex items-center justify-center gap-4 text-[11.5px] tracking-[0.4em] uppercase text-[#3b5675] dark:text-neutral-400 mb-8">
                <span className="h-px w-12 bg-current opacity-40" />Our Partners<span className="h-px w-12 bg-current opacity-40" />
              </div>
              <div data-testid="partners-grid" className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                {partners.map((p, i) => <PartnerCard key={p.id} p={p} i={i} />)}
              </div>
            </>
          )}
          {page.highlights?.length > 0 && <Highlights items={page.highlights} />}
        </div>
      </div>
    </main>
  );
};

export default PartnersPage;
